import { MIXPANEL_EVENTS, trackEvent } from '@/lib/mixpanel';
import { aiAnalysisStore } from '@/stores/aiAnalysisStore';
import { searchRequestStore } from '@/stores/searchRequestStore';
import { SubmitStatus, SubmitStatuses } from '@/util/storeUtilities';
import axios from 'axios';
import { toast } from 'react-toastify';
import { createUseStore } from 'wired-react';
import { createStore } from 'wired-store';
import { API_URL } from '~/modules/config';
import { serializeAxiosError } from '~/modules/utilities/axios_utils';
import { handleException } from '~/modules/utilities/cross_env_utils';
import { validateEmail, validatePhone } from '~/modules/utilities/validation_utils';
import { globalStores } from '@/util/globalStores';

type ValidationResult<T> = Partial<{
  [K in keyof T]: T[K] extends object
    ? Partial<ValidationResult<T[K]>> // Recursively apply to nested objects
    : string | null; // Replace non-objects with string | null
}>;

export type LeadGenInfoStoreState = {
  submitStatus: SubmitStatus;
  notifySuccess: string;
  notifyFailure: string;

  validationErrors: ValidationResult<LeadGenInfoStoreState>;
  userInformation: {
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
  };
  vehicleInformation: {
    make: string;
    model: string;
    year: string;
    mileage: string;
    state: string;
  };
};

export interface LeadUserInfo {}

const initialState: LeadGenInfoStoreState = {
  submitStatus: SubmitStatuses.UNINITIALIZED,
  notifySuccess: '',
  notifyFailure: '',

  validationErrors: {},
  userInformation: {
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
  },

  vehicleInformation: {
    make: '',
    model: '',
    year: '',
    mileage: '',
    state: '',
  },
};

const LOCAL_STORAGE_KEY = 'leadGenInfoStoreState';

export const leadGenInfoStore = createStore(
  initialState,
  (set, get) => ({
    changeUserInformation(changes: Partial<LeadGenInfoStoreState['userInformation']>) {
      set(function changeUserInformation(draft) {
        for (const key of Object.keys(changes)) {
          draft.userInformation[key] = changes[key];
          draft.validationErrors = validateFields(draft.userInformation);
        }
      });
    },
    changeVehicleInformation(changes: Partial<LeadGenInfoStoreState['vehicleInformation']>) {
      set(function changeVehicleInformation(draft) {
        for (const key of Object.keys(changes)) {
          draft.vehicleInformation[key] = changes[key];
        }
      });
    },
    clearNotifySuccess() {
      set(function clearNotifySuccess(draft) {
        draft.notifySuccess = '';
      });
    },
    clearNotifyFailure() {
      set(function clearNotifyFailure(draft) {
        draft.notifyFailure = '';
      });
    },

    async submitLeadInfo(useAiStoreSearchRequest) {
      try {
        const leadGenInfo = selectLeadGenInfo();
        const searchRequest = useAiStoreSearchRequest
          ? aiAnalysisStore.getState().searchRequest
          : searchRequestStore.getState().planListingResponse.searchRequest;
        const validationErrors = validateFields(leadGenInfo.userInformation);
        //Reject the submit request if there are validation errors
        if (Object.keys(validationErrors).length > 0) {
          set(function setInvalidFieldErrors(draft) {
            draft.validationErrors = validationErrors;
          });
          return;
        }
        // Setting the submit status here allows us to disable the submit button while it's processing
        set(function beginSubmit(draft) {
          draft.submitStatus = SubmitStatuses.SUBMITTING;
          draft.validationErrors = {};
          draft.notifySuccess = '';
          draft.notifyFailure = '';
        });

        const leadRequest = {
          email: leadGenInfo.userInformation.email,
          firstname: leadGenInfo.userInformation.firstName,
          lastname: leadGenInfo.userInformation.lastName,
          phone: leadGenInfo.userInformation.phone,

          company: 'WarrantyPilot',
          message: 'Lead from site lead form',

          vehicle_make: searchRequest.make,
          vehicle_model: searchRequest.model,
          vehicle_year: searchRequest.year,
          vehicle_mileage: searchRequest.mileage,
          state: searchRequest.state,
          hs_state_code: searchRequest.state,
        };

        await axios.post(`${API_URL}/subscribe/lead`, leadRequest);

        // You could just call subscribeStore.reset() here, which would do the same thing,
        // but I think this makes the code more explicit, and helps you see how async actions work in stores.
        // PS: Async actions usually have multiple set() calls, combined with one or more awaits.
        set(function completeSubmit(draft) {
          draft.submitStatus = SubmitStatuses.SUBMITTED;
          draft.notifySuccess = `Contact Information Submitted`;
          toast.success(draft.notifySuccess);
          trackEvent(MIXPANEL_EVENTS.COLLECTED_LEAD_INFO, leadRequest);
        });
      } catch (error) {
        //TODO: Handle API sending invalidation lead values
        handleException(error);
        set(function failedSubmit(draft) {
          draft.submitStatus = SubmitStatuses.FAILED;
          draft.notifyFailure = serializeAxiosError(error).message;
          toast.error(draft.notifyFailure);
        });
      }
    },
  }),
  {
    storeName: 'leadGenInfoPlanStore',
    attachTo: globalStores(),
    warnAgainstAnonymousActions: IS_DEVELOPMENT,
  },
);

try {
  if (typeof localStorage !== 'undefined') {
    const localStorageState = JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEY) ?? 'null');
    if (localStorageState) {
      leadGenInfoStore.setState(localStorageState);
    }
  }
} catch (error) {
  handleException(error);
}

// Save state to local storage
leadGenInfoStore.subscribe((state) => {
  if (typeof localStorage !== 'undefined') {
    localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(state));
  }
});

// This hook is used to connect react components to the store
export const useLeadGenInfoStore = createUseStore(leadGenInfoStore);

/**
 * Map the store state to the API DTO
 * @param state
 * @returns DTOType
 */
//These are object selectors, which means you need to provide the equalityFn when you use them.
export function selectLeadGenInfo(state: LeadGenInfoStoreState = leadGenInfoStore.getState()) {
  return {
    validationErrors: state.validationErrors,
    userInformation: state.userInformation,
    vehicleInformation: state.vehicleInformation,
  };
}

export function selectLeadGenInfoSubmitStatus(state: LeadGenInfoStoreState = leadGenInfoStore.getState()) {
  return { submitStatus: state.submitStatus, notifySuccess: state.notifySuccess, notifyFailure: state.notifyFailure };
}

export function validateFields(
  dto: LeadGenInfoStoreState['userInformation'],
): LeadGenInfoStoreState['validationErrors'] {
  const validationErrors: LeadGenInfoStoreState['validationErrors'] = {};
  const userInformationErrors: LeadGenInfoStoreState['validationErrors']['userInformation'] = {};

  //USER INFORMATION
  if (!dto.firstName.trim()) {
    userInformationErrors.firstName = 'First name is required';
  }
  if (!dto.lastName.trim()) {
    userInformationErrors.lastName = 'Last name is required';
  }

  if (!dto.email.trim()) {
    userInformationErrors.email = 'Email address is required';
  } else if (!validateEmail(dto.email)) {
    userInformationErrors.email = 'Invalid email address';
  }

  if (!dto.phone.trim()) {
    userInformationErrors.phone = 'Phone number is required';
  } else if (!validatePhone(dto.phone)) {
    userInformationErrors.phone = 'Invalid phone number. Format XXX-XXX-XXXX';
  }

  //Assign user information property only if errors exist
  if (Object.keys(userInformationErrors).length > 0) {
    validationErrors.userInformation = userInformationErrors;
  }

  return validationErrors;
}
